import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Chip,
  Alert,
  CircularProgress,
  Stack,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  MyLocation as MyLocationIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import UniversalMap from './maps/UniversalMap';
import { LatLng, UniversalMapMarker } from './maps/MapInterface';
import { US_STATES } from '../utils/usStates';
import { CreateDreamData, DreamType, DreamPriority } from '../types';
import { dreamsService } from '../services/dreamsService';
import { useNotification } from '../contexts/NotificationContext';

interface CreateDreamDialogProps {
  open: boolean;
  onClose: () => void;
  onDreamCreated: (dream: any) => void;
  initialData?: Partial<CreateDreamData>;
}

const CreateDreamDialog: React.FC<CreateDreamDialogProps> = ({
  open,
  onClose,
  onDreamCreated,
  initialData
}) => {
  const { showBadgeEarned } = useNotification();
  const [formData, setFormData] = useState<CreateDreamData>({
    title: initialData?.title || '',
    description: initialData?.description || '',
    latitude: initialData?.latitude || 0,
    longitude: initialData?.longitude || 0,
    location_name: initialData?.location_name || '',
    dream_type: initialData?.dream_type || 'destination',
    priority: initialData?.priority || 'medium',
    tags: initialData?.tags || [],
    best_time_to_visit: initialData?.best_time_to_visit || '',
    estimated_budget: initialData?.estimated_budget || 0,
    notes: initialData?.notes || '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newTag, setNewTag] = useState('');
  const [locationSearchValue, setLocationSearchValue] = useState('');
  
  // Search functionality  
  const [businessValue, setBusinessValue] = useState('');
  const [cityValue, setCityValue] = useState('');
  const [stateValue, setStateValue] = useState('');
  
  // Universal Map state
  const [selectedLocation, setSelectedLocation] = useState<{ lat: number; lng: number } | null>(
    initialData?.latitude && initialData?.longitude 
      ? { lat: initialData.latitude, lng: initialData.longitude }
      : null
  );
  
  // Create markers array for the map
  const locationMarkers = selectedLocation ? [{
    id: 'selected-location',
    position: selectedLocation,
    title: formData.location_name || 'Selected Location',
    color: '#2196F3'
  }] : [];

  // Handle location selection
  const handleLocationSelect = (
    location: { lat: number; lng: number },
    address?: string
  ) => {
    console.log('Location selected:', { location, address });
    
    // Update selected location state
    setSelectedLocation(location);
    
    // Update form data with location
    setFormData(prev => ({
      ...prev,
      latitude: location.lat,
      longitude: location.lng,
      location_name: address || `${location.lat.toFixed(6)}, ${location.lng.toFixed(6)}`,
    }));

    // Update search value with the address
    if (address) {
      setLocationSearchValue(address);
    }

    setError(null);
  };

  // Business search handler
  const handleBusinessSearch = async (business: string, city: string, state: string) => {
    if (!business.trim()) return;

    try {
      console.log('🔍 CreateDreamDialog: Searching for business:', business, 'in', city, state);
      
      // Use our search provider API for business search
      const response = await fetch('/api/search-providers/search', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: business,
          type: 'business',
          city: city,
          state: state,
          limit: 5
        })
      });
      const data = await response.json();
      
      console.log('🔍 CreateDreamDialog: Business search response:', data);
      
      if (data.success && data.results && data.results.length > 0) {
        const result = data.results[0];
        const location = {
          lat: result.latitude || result.lat,
          lng: result.longitude || result.lng
        };
        
        console.log('🔍 CreateDreamDialog: Found business location:', location, 'from provider:', result.provider);
        
        const placeName = result.name || result.display_name || result.formatted_address || business;
        handleLocationSelect(location, placeName);
      } else {
        console.warn('🔍 CreateDreamDialog: No results found for business:', business);
      }
    } catch (error) {
      console.error('🔍 CreateDreamDialog: Error in business search:', error);
    }
  };

  // City/State search handler
  const handleCityStateSearch = async (city: string, state: string) => {
    if (!city.trim() && !state.trim()) return;

    try {
      console.log('🔍 CreateDreamDialog: Searching for city/state:', city, state);
      
      // Use our search provider API for address search
      const searchQuery = [city, state].filter(Boolean).join(', ');
      const response = await fetch('/api/search-providers/search', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: searchQuery,
          type: 'address',
          city: city,
          state: state,
          limit: 5
        })
      });
      const data = await response.json();
      
      console.log('🔍 CreateDreamDialog: City/state search response:', data);
      
      if (data.success && data.results && data.results.length > 0) {
        const result = data.results[0];
        const location = {
          lat: result.latitude || result.lat,
          lng: result.longitude || result.lng
        };
        
        console.log('🔍 CreateDreamDialog: Found city/state location:', location, 'from provider:', result.provider);
        
        const placeName = result.name || result.display_name || result.formatted_address || `${city}, ${state}`;
        handleLocationSelect(location, placeName);
      } else {
        console.warn('🔍 CreateDreamDialog: No results found for city/state:', city, state);
      }
    } catch (error) {
      console.error('🔍 CreateDreamDialog: Error in city/state search:', error);
    }
  };

  // Handle current location
  const handleUseCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const location = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          handleLocationSelect(location, 'Current Location');
        },
        (error) => {
          console.error('Geolocation error:', error);
          setError('Unable to get your current location. Please check location permissions.');
        }
      );
    }
  };

  // Handle map click for Universal Map
  const handleMapClick = (location: { lat: number; lng: number }) => {
    setSelectedLocation(location);
    handleLocationSelect(location, `${location.lat.toFixed(6)}, ${location.lng.toFixed(6)}`);
  };

  // Handle location search for Universal Map
  const handleLocationSearch = (location: { lat: number; lng: number }, address: string) => {
    setSelectedLocation(location);
    handleLocationSelect(location, address);
  };

  // Handle form submission
  const handleSubmit = async () => {
    setLoading(true);
    setError(null);

    try {
      console.log('Creating dream with data:', formData);
      console.log('Form data JSON:', JSON.stringify(formData, null, 2));
      const response = await dreamsService.createDream(formData);
      console.log('Dream created successfully:', response);
      
      // Show badge notifications if any badges were awarded
      if (response.awardedBadges && response.awardedBadges.length > 0) {
        response.awardedBadges.forEach((badge: any) => {
          showBadgeEarned(badge.name);
        });
      }
      
      onDreamCreated(response.dream || response);
      onClose();
      
      // Reset form
      setFormData({
        title: '',
        description: '',
        latitude: 0,
        longitude: 0,
        location_name: '',
        dream_type: 'destination',
        priority: 'medium',
        tags: [],
        best_time_to_visit: '',
        estimated_budget: 0,
        notes: '',
      });
      setLocationSearchValue('');
    } catch (error) {
      console.error('Failed to create dream:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      console.error('Error details:', errorMessage);
      setError(`Failed to create dream: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle tag operations
  const handleAddTag = () => {
    if (newTag.trim() && formData.tags && !formData.tags.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...(prev.tags || []), newTag.trim()]
      }));
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: (prev.tags || []).filter(tag => tag !== tagToRemove)
    }));
  };

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      setError(null);
      setLocationSearchValue('');
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Create New Dream</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Basic Information */}
          <TextField
            fullWidth
            label="Dream Title"
            placeholder="What's your dream destination or experience?"
            value={formData.title}
            onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
            required
          />

          <TextField
            fullWidth
            label="Description"
            placeholder="Describe your dream..."
            value={formData.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            multiline
            rows={3}
          />

          {/* Dream Type and Priority */}
          <Stack direction="row" spacing={2}>
            <FormControl fullWidth>
              <InputLabel>Dream Type</InputLabel>
              <Select
                value={formData.dream_type}
                label="Dream Type"
                onChange={(e) => setFormData(prev => ({ ...prev, dream_type: e.target.value as DreamType }))}
              >
                <MenuItem value="destination">Destination</MenuItem>
                <MenuItem value="attraction">Attraction</MenuItem>
                <MenuItem value="restaurant">Restaurant</MenuItem>
                <MenuItem value="accommodation">Accommodation</MenuItem>
                <MenuItem value="activity">Activity</MenuItem>
                <MenuItem value="brewery">Brewery</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select
                value={formData.priority}
                label="Priority"
                onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value as DreamPriority }))}
              >
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="urgent">Urgent</MenuItem>
              </Select>
            </FormControl>
          </Stack>

          {/* Location Selection with Universal Map */}
          <Box>
            <Typography variant="h6" gutterBottom>
              Location
            </Typography>
            
            {/* Business Search */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom color="text.secondary">
                Search for Business or Attraction
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <TextField
                  fullWidth
                  label="Business or Place"
                  placeholder="e.g., Disney World, Starbucks, Best Buy"
                  helperText="Restaurant, store, attraction, etc."
                  size="small"
                  value={businessValue}
                  onChange={(e) => setBusinessValue(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleBusinessSearch(businessValue, cityValue, stateValue);
                    }
                  }}
                />
                <TextField
                  label="City (Optional)"
                  placeholder="e.g., Orlando"
                  size="small"
                  sx={{ minWidth: 140 }}
                  value={cityValue}
                  onChange={(e) => setCityValue(e.target.value)}
                />
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>State</InputLabel>
                  <Select
                    value={stateValue}
                    label="State"
                    onChange={(e) => setStateValue(e.target.value)}
                  >
                    <MenuItem value="">
                      <em>Any</em>
                    </MenuItem>
                    {US_STATES.map(state => (
                      <MenuItem key={state.value} value={state.value}>
                        {state.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => handleBusinessSearch(businessValue, cityValue, stateValue)}
                  disabled={!businessValue.trim()}
                  sx={{ minWidth: 100 }}
                >
                  Search
                </Button>
              </Box>
            </Box>

            {/* City and State fields for location-only search */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom color="text.secondary">
                Or Search by Location Only
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  label="City"
                  placeholder="e.g., Ormond Beach"
                  helperText="City to explore"
                  size="small"
                  value={cityValue}
                  onChange={(e) => setCityValue(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleCityStateSearch(cityValue, stateValue);
                    }
                  }}
                />
                <FormControl fullWidth size="small">
                  <InputLabel>State</InputLabel>
                  <Select
                    value={stateValue}
                    label="State"
                    onChange={(e) => setStateValue(e.target.value)}
                  >
                    <MenuItem value="">
                      <em>Any</em>
                    </MenuItem>
                    {US_STATES.map(state => (
                      <MenuItem key={state.value} value={state.value}>
                        {state.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => handleCityStateSearch(cityValue, stateValue)}
                  disabled={!cityValue.trim() && !stateValue.trim()}
                  sx={{ minWidth: 100 }}
                >
                  Go to City
                </Button>
                <Button
                  variant="text"
                  size="small"
                  onClick={() => {
                    setBusinessValue('');
                    setCityValue('');
                    setStateValue('');
                  }}
                  sx={{ minWidth: 80 }}
                >
                  Clear All
                </Button>
              </Box>
            </Box>
            
            {/* Current Location Button */}
            <Box sx={{ mb: 2 }}>
              <Button
                variant="outlined"
                startIcon={<MyLocationIcon />}
                onClick={handleUseCurrentLocation}
                size="small"
              >
                Use Current Location
              </Button>
            </Box>

            {/* Universal Map */}
            <Box sx={{ height: 400, border: '1px solid #ddd', borderRadius: 1 }}>
              <UniversalMap
                center={selectedLocation || { lat: 39.8283, lng: -98.5795 }}
                zoom={selectedLocation ? 15 : 4}
                markers={locationMarkers}
                onMapClick={handleMapClick}
                onLocationSearch={handleLocationSearch}
                showSearch={false}
                showControls={true}
                interactive={true}
                style={{ height: '100%', width: '100%' }}
              />
            </Box>
            
            {selectedLocation && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Selected: {formData.location_name} ({selectedLocation.lat.toFixed(6)}, {selectedLocation.lng.toFixed(6)})
              </Typography>
            )}
          </Box>

          {/* Tags */}
          <Box>
            <Typography variant="h6" gutterBottom>
              Tags
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
              <TextField
                size="small"
                label="Add tag"
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddTag();
                  }
                }}
              />
              <IconButton onClick={handleAddTag} disabled={!newTag.trim()}>
                <AddIcon />
              </IconButton>
            </Stack>
            
            {formData.tags && formData.tags.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {formData.tags.map((tag) => (
                  <Chip
                    key={tag}
                    label={tag}
                    onDelete={() => handleRemoveTag(tag)}
                    deleteIcon={<DeleteIcon />}
                    size="small"
                  />
                ))}
              </Box>
            )}
          </Box>

          {/* Additional Details */}
          <Stack direction="row" spacing={2}>
            <TextField
              type="date"
              label="Best Time to Visit"
              InputLabelProps={{ shrink: true }}
              value={formData.best_time_to_visit}
              onChange={(e) => setFormData(prev => ({ ...prev, best_time_to_visit: e.target.value }))}
              fullWidth
            />
            
            <TextField
              type="number"
              label="Estimated Budget ($)"
              value={formData.estimated_budget || ''}
              onChange={(e) => setFormData(prev => ({ 
                ...prev, 
                estimated_budget: e.target.value ? parseFloat(e.target.value) : 0 
              }))}
              fullWidth
              inputProps={{ min: 0, step: 0.01 }}
            />
          </Stack>

          <TextField
            fullWidth
            label="Notes"
            placeholder="Any additional notes or details..."
            value={formData.notes}
            onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
            multiline
            rows={3}
          />
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !formData.title.trim() || !formData.latitude || !formData.longitude}
          startIcon={loading ? <CircularProgress size={20} /> : undefined}
        >
          {loading ? 'Creating...' : 'Create Dream'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateDreamDialog;